import { Check } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const TERMS_URL = '/terms';
const PRIVACY_URL = '/privacy';

interface TermsStepProps {
    onAccept: () => void;
    onDecline: () => void;
}

interface CheckRowProps {
    checked: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}

const CheckRow: React.FC<CheckRowProps> = ({ checked, onToggle, children }) => {
    // Names the checkbox after the sibling sentence (WCAG 4.1.2).
    const labelId = React.useId();
    return (
        <div className="flex items-start gap-3">
            <button
                type="button"
                role="checkbox"
                aria-checked={checked}
                aria-labelledby={labelId}
                onClick={onToggle}
                className={cn(
                    'mt-0.5 flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-md border transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                    checked
                        ? 'border-primary bg-primary text-primary-foreground'
                        : // border-muted-foreground: the unchecked box must hold
                    // ≥3:1 against the card (WCAG 1.4.11); --border is 1.2:1.
                        'border-muted-foreground bg-transparent hover:border-foreground',
                )}
            >
                {checked && <Check size={13} strokeWidth={3} />}
            </button>
            <p
                id={labelId}
                className="cursor-pointer select-none text-sm leading-relaxed text-muted-foreground"
                onClick={(e) => {
                    // Let embedded policy links navigate without toggling.
                    if ((e.target as HTMLElement).closest('a')) return;
                    onToggle();
                }}
            >
                {children}
            </p>
        </div>
    );
};

/**
 * Step 2 — the hard gate: terms, privacy, restricted-jurisdiction attestation
 * and cookie acceptance.
 */
const TermsStep: React.FC<TermsStepProps> = ({ onAccept, onDecline }) => {
    const [termsChecked, setTermsChecked] = useState(false);
    const [jurisdictionChecked, setJurisdictionChecked] = useState(false);
    const [cookiesChecked, setCookiesChecked] = useState(false);

    return (
        <div className="px-6 pb-6 pt-2">
            <h2 className="text-center text-xl font-bold tracking-tight text-foreground">
                Terms of Use, Privacy Policy, and Cookie Policy
            </h2>
            <p className="mt-1 text-center text-sm text-muted-foreground">
                To proceed, review and accept the following:
            </p>

            <div className="mt-6 flex flex-col gap-4">
                <CheckRow
                    checked={termsChecked}
                    onToggle={() => setTermsChecked((v) => !v)}
                >
                    You acknowledge that you have read, understood, and agreed
                    to the{' '}
                    <a
                        href={TERMS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-info underline underline-offset-2 hover:text-info/80"
                    >
                        Terms of Use
                    </a>{' '}
                    and{' '}
                    <a
                        href={PRIVACY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-info underline underline-offset-2 hover:text-info/80"
                    >
                        Privacy Policy
                    </a>
                    .
                </CheckRow>

                <CheckRow
                    checked={jurisdictionChecked}
                    onToggle={() => setJurisdictionChecked((v) => !v)}
                >
                    I confirm that I am not a citizen or resident of the United
                    States, that I am not located in or accessing Snappy from
                    the United States, and that I am not using Snappy on behalf
                    of any United States person or entity. Snappy is not yet
                    available in the United States (regulatory approval
                    pending).
                </CheckRow>

                <CheckRow
                    checked={cookiesChecked}
                    onToggle={() => setCookiesChecked((v) => !v)}
                >
                    This site uses cookies to ensure the best user experience.
                    These cookies are strictly necessary or essential for
                    optimal functionality. By using this site, you agree to the
                    cookie policy.
                </CheckRow>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3">
                <Button variant="secondary" size="lg" onClick={onDecline}>
                    Decline
                </Button>
                <Button
                    size="lg"
                    className="font-semibold"
                    disabled={
                        !termsChecked || !jurisdictionChecked || !cookiesChecked
                    }
                    onClick={onAccept}
                >
                    Accept
                </Button>
            </div>
        </div>
    );
};

export default TermsStep;
